import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ChevronRight } from 'lucide-react';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import DOMPurify from 'dompurify';
import { useReduxOrg } from '@Hooks/reduxOrgHook/useReduxOrg';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import OrgService from '@Helpers/URLHelpers/OrgService';
import UserAuthService from '@Helpers/URLHelpers/UserAuthService';
import SnackbarManager from '@CommonFunctionalDisplay/GlobalSnackbar/SnackbarManager';
import './OrgSelectionPage.scss';

export default function OrgSelectionPage() {

    const navigate = useNavigate();
    const { setCurrentOrg } = useReduxOrg();
    const { setCurrentProject } = useReduxProject();
    const { activeUser, activeUserId, activeUserAccessList, setActiveUserAccessList } = useReduxActiveUser();
    const [selecting, setSelecting] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [newOrgData, setNewOrgData] = useState({ orgName: '', defaultResolveTag: '' });
    const [newOrgErrors, setNewOrgErrors] = useState({});

    const orgService = new OrgService();
    const userAuthService = new UserAuthService();

    const resolveOrgs = (activeUserAccessList?.orgs || []).filter(o => o.visibility === 'resolve');

    const className = 'orgSelectionPage';

    const refreshAccessList = async () => {
        const response = await userAuthService.getAccessListForUser(activeUserId);
        const updated = JSON.parse(response.body);
        setActiveUserAccessList(updated);
        return updated;
    };

    const validateNewOrgForm = () => {
        const errors = {};
        const tagValue = (newOrgData.defaultResolveTag || '').trim();
        if (!(newOrgData.orgName || '').trim()) {
            errors.orgName = 'Organization Name is required';
        }
        if (!tagValue) {
            errors.defaultResolveTag = 'Default Form Tag is required';
        } else if (tagValue.length !== 6) {
            errors.defaultResolveTag = 'Tag must be exactly 6 characters';
        } else if (!/^[A-Z]{6}$/.test(tagValue)) {
            errors.defaultResolveTag = 'Tag must be 6 uppercase letters';
        }
        setNewOrgErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleCreateOrg = async () => {
        if (!validateNewOrgForm()) return;

        setCreating(true);
        try {
            const response = await orgService.createOrgForResolveUser({
                userId: activeUserId,
                user: activeUser,
                org: {
                    orgName: DOMPurify.sanitize(newOrgData.orgName).trim(),
                    defaultResolveTag: DOMPurify.sanitize(newOrgData.defaultResolveTag).trim().toUpperCase()
                }
            });

            if (response.status >= 400) {
                SnackbarManager.show('Failed to create workspace', 'error');
                return;
            }

            await refreshAccessList();
            setCreateOpen(false);
            setNewOrgData({ orgName: '', defaultResolveTag: '' });
            setNewOrgErrors({});
            SnackbarManager.show('Workspace created successfully!', 'success');
            navigate('/subscribe', { replace: true });
        } catch (err) {
            console.error(err);
            SnackbarManager.show('Error creating workspace', 'error');
        } finally {
            setCreating(false);
        }
    };

    const handleSelect = async (org) => {
        if (selecting !== null) return;
        setSelecting(org.id);
        try {
            await setCurrentOrg(org);
            const resolveProject = (activeUserAccessList?.projects || []).find(
                p => p.visibility === 'resolve' && p.parentId === org.id
            );
            if (resolveProject) {
                await setCurrentProject(resolveProject);
            }
            navigate('/dashboard', { replace: true });
        } catch (err) {
            console.error(err);
            setSelecting(null);
        }
    };

    return (
        <div className={className}>
            <header className={className + '__header'}>
                <h1 className={className + '__logo'}>KaizIQ</h1>
            </header>

            <main className={className + '__main'}>
                <div className={className + '__card'}>
                    <div className={className + '__card__icon'}>
                        <Building2 size={36} />
                    </div>
                    <h2 className={className + '__card__title'}>Select a Workspace</h2>
                    <p className={className + '__card__subtitle'}>
                        You have access to multiple Resolve workspaces. Choose one to continue.
                    </p>

                    <div className={className + '__actions'}>
                        <button
                            type='button'
                            className={className + '__createBtn'}
                            onClick={() => setCreateOpen(true)}
                        >
                            Create Workspace
                        </button>
                    </div>

                    <div className={className + '__list'}>
                        {resolveOrgs.map(org => (
                            <button
                                key={org.id}
                                className={className + '__org' + (selecting === org.id ? ' ' + className + '__org--loading' : '')}
                                onClick={() => handleSelect(org)}
                                disabled={selecting !== null}
                            >
                                <div className={className + '__org__avatar'}>
                                    {org.orgName?.[0]?.toUpperCase() || 'O'}
                                </div>
                                <div className={className + '__org__info'}>
                                    <span className={className + '__org__name'}>{org.orgName}</span>
                                    <span className={className + '__org__tag'}>{org.tag}</span>
                                </div>
                                <div className={className + '__org__action'}>
                                    {selecting === org.id
                                        ? <CircularProgress size={16} color="inherit" />
                                        : <ChevronRight size={18} />
                                    }
                                </div>
                            </button>
                        ))}

                        {resolveOrgs.length === 0 && (
                            <p className={className + '__empty'}>No Resolve workspaces found.</p>
                        )}
                    </div>
                </div>
            </main>

            <footer className={className + '__footer'}>
                © {new Date().getFullYear()} KaizIQ Inc. All rights reserved.
            </footer>

            <Dialog
                open={createOpen}
                onClose={() => !creating && setCreateOpen(false)}
                maxWidth='sm'
                fullWidth
                PaperProps={{ className: className + '__dialogPaper' }}
            >
                <DialogTitle className={className + '__dialogTitle'}>
                    <div className={className + '__dialogTitleRow'}>
                        <div className={className + '__dialogTitleIcon'}>
                            <Building2 size={18} />
                        </div>
                        <div>
                            <h3 className={className + '__dialogHeading'}>Create New Workspace</h3>
                            <p className={className + '__dialogSubtitle'}>Set up your organization and default form tag.</p>
                        </div>
                    </div>
                </DialogTitle>
                <DialogContent className={className + '__dialogContent'}>
                    <TextField
                        fullWidth
                        margin='normal'
                        label='Organization Name'
                        placeholder='e.g. Acme Corp'
                        value={newOrgData.orgName}
                        onChange={(e) => {
                            setNewOrgData(prev => ({ ...prev, orgName: e.target.value }));
                            if (newOrgErrors.orgName) setNewOrgErrors(prev => ({ ...prev, orgName: '' }));
                        }}
                        error={!!newOrgErrors.orgName}
                        helperText={newOrgErrors.orgName}
                        disabled={creating}
                    />
                    <TextField
                        fullWidth
                        margin='normal'
                        label='Default Form Tag'
                        placeholder='6 uppercase letters (e.g. ACMECR)'
                        value={newOrgData.defaultResolveTag}
                        onChange={(e) => {
                            const value = e.target.value.toUpperCase().slice(0, 6);
                            setNewOrgData(prev => ({ ...prev, defaultResolveTag: value }));
                            if (newOrgErrors.defaultResolveTag) setNewOrgErrors(prev => ({ ...prev, defaultResolveTag: '' }));
                        }}
                        inputProps={{ maxLength: 6 }}
                        error={!!newOrgErrors.defaultResolveTag}
                        helperText={newOrgErrors.defaultResolveTag || 'Tag is used for default form prefix'}
                        disabled={creating}
                    />
                    <div className={className + '__dialogHint'}>
                        Your tag is used as a short identifier on default forms.
                    </div>
                </DialogContent>
                <DialogActions className={className + '__dialogActions'}>
                    <button
                        type='button'
                        className={className + '__dialogCancel'}
                        onClick={() => setCreateOpen(false)}
                        disabled={creating}
                    >
                        Cancel
                    </button>
                    <button
                        type='button'
                        className={className + '__dialogCreate'}
                        onClick={handleCreateOrg}
                        disabled={creating}
                    >
                        {creating ? 'Creating...' : 'Create Workspace'}
                    </button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
